import { useEffect, useId, useMemo, useState } from 'react'
import { ChevronIcon, OneMarkIcon } from '../icons'
import { TraySection } from '../shop/TraySection'
import { messageOf } from '../../lib/errors'
import { useHubLang, type HubLang } from '../../lib/i18n'
import {
  acceptTitleSorts,
  confirmFunctions,
  decideTitleSort,
  functionName,
  loadFunctions,
  loadTitleSorts,
  retireFunction,
  saveFunction,
  titleState,
  type JobFunction,
  type TitleSort,
  type TitleState,
} from '../../lib/roleSuggestions'

/**
 * Job titles and functions (154; reshaped 200) — the company-wide vocabulary
 * typical holders match on. Group administrators only (the server refuses a
 * region).
 *
 * TWO LISTS, ONE TRAY. The functions are HR's list (Lisa, Soumya): it starts
 * as an assistant's draft, and saving a function is confirming it. The titles
 * are BambooHR's: OneConetec suggests a function for each new one, and only a
 * sort a person accepted is ever matched on. A rejected sort stays rejected —
 * the title is not sent to OneConetec again, and HR sorts it by hand here.
 *
 * ONE LANGUAGE FIRST (200, David 18 Oct). A function is named in the reader's
 * profile language; the other language is a small "+ Add Spanish" / "+ Add
 * English" until somebody adds it. A missing translation is NULL on the
 * server, never a copy, so "not translated yet" and "the same word in both"
 * (Marketing) are different rows. A new function's key is made from its name
 * on the server; nobody types a key.
 *
 * ONE LINE PER FUNCTION. The chevron opens the row: its key, the titles sorted
 * into it (each with Move to…), and Retire, which is rare and so lives there.
 * The line itself carries only the name, the count, the draft pill and the
 * one button that matters (Confirm, or Save once something changed).
 *
 * Teal is OneConetec's suggestion and nothing else (the AI disclosure).
 */
export function JobTitleSorts({
  initial,
}: {
  /** The render suite's way in: effects do not run under renderToString. */
  initial?: { functions: JobFunction[]; titles: TitleSort[] }
}) {
  const lang = useHubLang()
  const [functions, setFunctions] = useState<JobFunction[] | null>(initial?.functions ?? null)
  const [titles, setTitles] = useState<TitleSort[] | null>(initial?.titles ?? null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // The render suite opens both sections, and every row, without a click
  // (062's lesson: an assertion against a shut section passes against nothing).
  const [open, setOpen] = useState<Set<string>>(new Set(initial ? ['functions', 'titles'] : ['titles']))
  const [show, setShow] = useState<TitleState>('to_check')

  async function reload() {
    try {
      const [f, t] = await Promise.all([loadFunctions(), loadTitleSorts()])
      setFunctions(f)
      setTitles(t)
      setError(null)
    } catch (e) {
      setError(messageOf(e))
    }
  }
  useEffect(() => {
    if (!initial) void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function run(token: string, fn: () => Promise<unknown>) {
    setBusy(token)
    setError(null)
    try {
      await fn()
      await reload()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  const live = (functions ?? []).filter((f) => !f.retired)
  const label = (key: string | null) => {
    const f = functions?.find((x) => x.key === key)
    return f ? functionName(f, lang) : key ?? ''
  }
  const counts = useMemo(() => {
    const c: Record<TitleState, number> = { to_check: 0, unsorted: 0, sorted: 0 }
    for (const t of titles ?? []) c[titleState(t)]++
    return c
  }, [titles])
  // Titles per function: accepted first, then OneConetec's suggestions.
  const byFunction = useMemo(() => {
    const m = new Map<string, TitleSort[]>()
    for (const t of titles ?? []) {
      if (!t.status || !t.functionKey) continue
      const list = m.get(t.functionKey) ?? []
      list.push(t)
      m.set(t.functionKey, list)
    }
    for (const list of m.values()) list.sort((a, b) => (a.status === b.status ? 0 : a.status === 'accepted' ? -1 : 1))
    return m
  }, [titles])
  const shown = (titles ?? []).filter((t) => titleState(t) === show)
  const draftKeys = live.filter((f) => !f.confirmed).map((f) => f.key)
  const drafts = draftKeys.length
  const untranslated = live.filter((f) => !f.labelEn?.trim() || !f.labelEs?.trim()).length
  const move = (jobTitle: string, key: string) => run(`t:${jobTitle}`, () => decideTitleSort(jobTitle, 'set', key))
  const toggle = (k: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })

  return (
    <div className="sorts">
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      <TraySection
        title="Functions"
        summary={
          functions === null
            ? 'Loading…'
            : [
                `${live.length} functions`,
                drafts ? `${drafts} ${drafts === 1 ? 'draft' : 'drafts'} for HR to confirm` : '',
                untranslated ? `${untranslated} not translated` : '',
              ]
                .filter(Boolean)
                .join(' · ')
        }
        open={open.has('functions')}
        onToggle={() => toggle('functions')}
      >
        <p className="admin__muted sorts__intro">
          The kinds of work job titles are sorted into. Name each in your language; the other language can wait. Open a
          function to see the titles in it.
        </p>
        {drafts > 0 && (
          <div className="sorts__bulk">
            <p>
              <strong>
                {drafts} {drafts === 1 ? 'draft' : 'drafts'}
              </strong>{' '}
              written by an assistant. Confirming says a person has read {drafts === 1 ? 'it' : 'them'}.
            </p>
            <button type="button" className="btn btn--primary btn--sm" disabled={busy !== null}
              onClick={() => run('fn:all', () => confirmFunctions(draftKeys))}>
              {busy === 'fn:all' ? '…' : drafts === 1 ? 'Confirm the draft' : `Confirm all ${drafts}`}
            </button>
          </div>
        )}
        <ul className="sorts__fns">
          {live.map((f) => (
            <FunctionRow key={f.key} f={f} lang={lang} titles={byFunction.get(f.key) ?? []} functions={live}
              busy={busy} openAtFirst={!!initial}
              onSave={(next) => run(`fn:${f.key}`, () => saveFunction(next))}
              onRetire={() => run(`retire:${f.key}`, () => retireFunction(f.key))}
              onAcceptAll={() => run(`acc:${f.key}`, () => acceptTitleSorts(f.key))}
              onMove={move} />
          ))}
        </ul>
        <NewFunction lang={lang} busy={busy} onAdd={(f) => run('fn:new', () => saveFunction(f))} />
      </TraySection>

      <TraySection
        title="Job titles"
        summary={titles === null ? 'Loading…' : `${counts.to_check} to check · ${counts.unsorted} unsorted · ${counts.sorted} sorted`}
        open={open.has('titles')}
        onToggle={() => toggle('titles')}
      >
        <div className="sorts__filter" role="group" aria-label="Which titles">
          {(['to_check', 'unsorted', 'sorted'] as const).map((k) => (
            <button key={k} type="button" className={`chip chip--sm${show === k ? ' chip--on' : ''}`} aria-pressed={show === k} onClick={() => setShow(k)}>
              {k === 'to_check' ? 'To check' : k === 'unsorted' ? 'Unsorted' : 'Sorted'} · {counts[k]}
            </button>
          ))}
        </div>
        {shown.length === 0 && titles !== null && (
          <p className="empty">
            {show === 'to_check'
              ? 'Nothing waiting. OneConetec sorts new titles when someone opens the Suggestions tab.'
              : show === 'unsorted'
                ? 'Every title has a function.'
                : 'No title is sorted yet.'}
          </p>
        )}
        <ul className="sorts__titles">
          {shown.map((t) => (
            <TitleRow key={t.jobTitle} t={t} functions={live} lang={lang} label={label} busy={busy}
              onAccept={() => run(`t:${t.jobTitle}`, () => decideTitleSort(t.jobTitle, 'accept', null))}
              onReject={() => run(`t:${t.jobTitle}`, () => decideTitleSort(t.jobTitle, 'reject', null))}
              onSet={(key) => move(t.jobTitle, key)} />
          ))}
        </ul>
      </TraySection>
    </div>
  )
}

const LANG_NAME: Record<HubLang, string> = { en: 'English', es: 'Spanish' }
const other = (lang: HubLang): HubLang => (lang === 'es' ? 'en' : 'es')
/** Split a function's two names into "mine" (the reader's) and "theirs". */
const namesOf = (f: Pick<JobFunction, 'labelEn' | 'labelEs'>, lang: HubLang) =>
  lang === 'es' ? { mine: f.labelEs ?? '', theirs: f.labelEn ?? '' } : { mine: f.labelEn ?? '', theirs: f.labelEs ?? '' }
const asLabels = (mine: string, theirs: string, lang: HubLang) =>
  lang === 'es' ? { labelEn: theirs.trim() || null, labelEs: mine.trim() || null } : { labelEn: mine.trim() || null, labelEs: theirs.trim() || null }

function FunctionRow({
  f,
  lang,
  titles,
  functions,
  busy,
  openAtFirst,
  onSave,
  onRetire,
  onAcceptAll,
  onMove,
}: {
  f: JobFunction
  lang: HubLang
  titles: TitleSort[]
  functions: JobFunction[]
  busy: string | null
  openAtFirst: boolean
  onSave: (f: { key: string; labelEn: string | null; labelEs: string | null; sortOrder: number }) => void
  onRetire: () => void
  /** 303. Accept every one of OneConetec's suggested sorts into this function. */
  onAcceptAll: () => void
  onMove: (jobTitle: string, key: string) => void
}) {
  const saved = namesOf(f, lang)
  const [mine, setMine] = useState(saved.mine)
  const [theirs, setTheirs] = useState(saved.theirs)
  const [adding, setAdding] = useState(false)
  const [open, setOpen] = useState(openAtFirst)
  // A reload after a save hands new names in; take them unless mid-edit.
  useEffect(() => {
    setMine(saved.mine)
    setTheirs(saved.theirs)
    setAdding(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.labelEn, f.labelEs])
  const moreId = useId()
  const name = functionName(f, lang)
  const changed = mine.trim() !== saved.mine.trim() || theirs.trim() !== saved.theirs.trim()
  const named = !!(mine.trim() || theirs.trim())
  const showTheirs = saved.theirs.trim() !== '' || adding
  const accepted = titles.filter((t) => t.status === 'accepted').length
  const toCheck = titles.length - accepted
  const working = busy !== null
  const save = () => onSave({ key: f.key, ...asLabels(mine, theirs, lang), sortOrder: f.sortOrder })

  return (
    <li className={`sorts__fn${open ? ' is-open' : ''}${f.confirmed ? '' : ' is-draft'}`}>
      <button type="button" className="sorts__toggle" aria-expanded={open} aria-controls={moreId} onClick={() => setOpen(!open)}>
        <ChevronIcon size={16} />
        <span className="visually-hidden">{open ? 'Hide' : 'Show'} the titles in {name}</span>
      </button>
      <div className="sorts__names">
        <label className="admin__field sorts__namefield">
          <span className="visually-hidden">{LANG_NAME[lang]} name of {name}</span>
          <input value={mine} lang={lang} placeholder={saved.mine ? undefined : `Not in ${LANG_NAME[lang]} yet — add it`}
            onChange={(e) => setMine(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && changed && named && !working) save() }} />
        </label>
        {showTheirs ? (
          <label className="admin__field sorts__trans">
            <span className="sorts__lang" aria-hidden="true">{other(lang).toUpperCase()}</span>
            <span className="visually-hidden">{LANG_NAME[other(lang)]} name of {name}</span>
            <input value={theirs} lang={other(lang)} autoFocus={adding} placeholder={`${LANG_NAME[other(lang)]} name`}
              onChange={(e) => setTheirs(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && changed && named && !working) save() }} />
          </label>
        ) : (
          <button type="button" className="linkbtn sorts__add" onClick={() => setAdding(true)}>
            + Add {LANG_NAME[other(lang)]}
          </button>
        )}
      </div>
      <div className="sorts__side">
        <button type="button" className="sorts__count" aria-expanded={open} aria-controls={moreId} onClick={() => setOpen(!open)}>
          {accepted} title{accepted === 1 ? '' : 's'}
          {toCheck > 0 && <span className="sorts__tocheck"> · {toCheck} to check</span>}
        </button>
        {!f.confirmed && <span className="pill pill--draft">Draft</span>}
        {(changed || !f.confirmed) && (
          <button type="button" className="btn btn--primary btn--xs" disabled={working || !named} onClick={save}>
            {busy === `fn:${f.key}` ? '…' : f.confirmed ? 'Save' : changed ? 'Save and confirm' : 'Confirm'}
          </button>
        )}
      </div>
      {open && (
        <div className="sorts__more" id={moreId}>
          <p className="sorts__keyline">
            Key <code>{f.key}</code> — it never changes; the names do.
          </p>
          {/* 303. One click per title was why 257 sorts sat unaccepted on prod for eleven days. */}
          {toCheck > 0 && (
            <div className="sorts__bulk">
              <p>
                <strong>{toCheck}</strong> {toCheck === 1 ? 'title' : 'titles'} OneConetec sorted into {name}, waiting for a person.
                Nothing matches on a suggestion; accepting says you have read {toCheck === 1 ? 'it' : 'them'}.
              </p>
              <button type="button" className="btn btn--primary btn--xs" disabled={working} onClick={onAcceptAll}>
                {busy === `acc:${f.key}` ? '…' : toCheck === 1 ? 'Accept it' : `Accept all ${toCheck}`}
              </button>
            </div>
          )}
          {titles.length === 0 ? (
            <p className="admin__muted sorts__none">No job title is sorted into {name} yet.</p>
          ) : (
            <ul className="sorts__in">
              {titles.map((t) => (
                <li key={t.jobTitle} className="sorts__intitle">
                  <span className="sorts__name">
                    {t.jobTitle}
                    <span className="admin__muted"> · {t.people} {t.people === 1 ? 'person' : 'people'}</span>
                  </span>
                  {t.status === 'suggested' && (
                    <span className="suggest__machine">
                      <OneMarkIcon size={12} /> OneConetec’s suggestion, to check
                    </span>
                  )}
                  <MoveTo t={t} functions={functions} lang={lang} working={working} verb="Move to…" onSet={(key) => onMove(t.jobTitle, key)} />
                </li>
              ))}
            </ul>
          )}
          <div className="sorts__retire">
            <button type="button" className="btn btn--quiet btn--xs" disabled={working || titles.length > 0} onClick={onRetire}>
              Retire
            </button>
            <span className="admin__muted">
              {titles.length > 0 ? 'Move its titles to another function first.' : 'Nothing is sorted into it, so it can be retired.'}
            </span>
          </div>
        </div>
      )}
    </li>
  )
}

function NewFunction({
  lang,
  busy,
  onAdd,
}: {
  lang: HubLang
  busy: string | null
  onAdd: (f: { key: null; labelEn: string | null; labelEs: string | null; sortOrder: number }) => void
}) {
  const [mine, setMine] = useState('')
  const [theirs, setTheirs] = useState('')
  const [adding, setAdding] = useState(false)
  return (
    <form
      className="sorts__new"
      onSubmit={(e) => {
        e.preventDefault()
        if (!mine.trim()) return
        onAdd({ key: null, ...asLabels(mine, theirs, lang), sortOrder: 200 })
        setMine('')
        setTheirs('')
        setAdding(false)
      }}
    >
      <div className="sorts__names">
        <label className="admin__field sorts__namefield">
          <span className="admin__searchlabel">New function</span>
          <input value={mine} lang={lang} placeholder={lang === 'es' ? 'p. ej. Perforación' : 'e.g. Drilling'} onChange={(e) => setMine(e.target.value)} />
        </label>
        {adding ? (
          <label className="admin__field sorts__trans">
            <span className="sorts__lang" aria-hidden="true">{other(lang).toUpperCase()}</span>
            <span className="visually-hidden">{LANG_NAME[other(lang)]} name</span>
            <input value={theirs} lang={other(lang)} autoFocus placeholder={`${LANG_NAME[other(lang)]} name`} onChange={(e) => setTheirs(e.target.value)} />
          </label>
        ) : (
          <button type="button" className="linkbtn sorts__add" onClick={() => setAdding(true)}>
            + Add {LANG_NAME[other(lang)]}
          </button>
        )}
      </div>
      <button type="submit" className="btn btn--quiet btn--sm" disabled={busy !== null || !mine.trim()}>
        Add a function
      </button>
    </form>
  )
}

function MoveTo({
  t,
  functions,
  lang,
  working,
  verb,
  onSet,
}: {
  t: TitleSort
  functions: JobFunction[]
  lang: HubLang
  working: boolean
  verb: string
  onSet: (key: string) => void
}) {
  const [pick, setPick] = useState('')
  return (
    <span className="sorts__move">
      <label className="admin__field sorts__pick">
        <span className="visually-hidden">Sort “{t.jobTitle}” by hand</span>
        <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={working}>
          <option value="">{verb}</option>
          {functions.filter((f) => f.key !== t.functionKey).map((f) => (
            <option key={f.key} value={f.key}>{functionName(f, lang)}</option>
          ))}
        </select>
      </label>
      <button type="button" className="btn btn--quiet btn--xs" disabled={working || !pick}
        onClick={() => { onSet(pick); setPick('') }}>
        Set
      </button>
    </span>
  )
}

function TitleRow({
  t,
  functions,
  lang,
  label,
  busy,
  onAccept,
  onReject,
  onSet,
}: {
  t: TitleSort
  functions: JobFunction[]
  lang: HubLang
  label: (key: string | null) => string
  busy: string | null
  onAccept: () => void
  onReject: () => void
  onSet: (key: string) => void
}) {
  const working = busy !== null
  return (
    <li className="sorts__title">
      <span className="sorts__name">
        {t.jobTitle}
        <span className="admin__muted"> · {t.people} {t.people === 1 ? 'person' : 'people'}</span>
      </span>
      {t.status === 'suggested' && (
        <span className="suggest__machine">
          <OneMarkIcon size={12} /> OneConetec: {label(t.functionKey)}
          {t.confidence !== null && ` (${Math.round(t.confidence * 100)}%)`}
        </span>
      )}
      {t.status === 'accepted' && (
        <span className="sorts__sorted">
          {label(t.functionKey)} · {t.sortedBy === 'one' ? 'OneConetec’s sort, accepted by HR' : 'sorted by HR'}
        </span>
      )}
      {t.rejected.length > 0 && (
        <span className="admin__muted sorts__rejected">Rejected: {t.rejected.map(label).join(', ')}</span>
      )}
      <span className="sorts__acts">
        {t.status === 'suggested' && (
          <>
            <button type="button" className="btn btn--primary btn--xs" disabled={working} onClick={onAccept}>Accept</button>
            <button type="button" className="btn btn--quiet btn--xs" disabled={working} onClick={onReject}>Reject</button>
          </>
        )}
        {t.status === 'accepted' && (
          <button type="button" className="btn btn--quiet btn--xs" disabled={working} onClick={onReject}>Reject</button>
        )}
        <MoveTo t={t} functions={functions} lang={lang} working={working} verb={t.status ? 'Move to…' : 'Sort into…'} onSet={onSet} />
      </span>
    </li>
  )
}
